import React, { useEffect, useState } from 'react'
import { ArrowRight, Fingerprint } from 'lucide-react'
import FormField from '@/components/common/FormField'
import { useForm } from 'react-hook-form'
import { errorTypes } from '@/utility/validation'
import { Button } from '@/components/ui/button'
import { Link, useSearchParams } from 'react-router-dom'
import { RoutesLink } from '@/utility/RouteLinks'
import { v4 as uuidv4 } from 'uuid';
import { AuthService } from '@/services/Auth/Auth'
import type { ILoginData } from '@/interface/auth.model'
import { getCookies, setCookies, showToastNotification } from '@/utility/helper'
import { ThrowError } from '@/api/callApi'
import axios from 'axios'
import { API_URL, API_ENDPOINT } from '@/api/constant'
import { Spinner } from '@/components/ui/spinner'


type Inputs = {
    email: string
    password: string,
    deviceId: string
}

const SsoLogin: React.FC = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Inputs>({ mode: 'onBlur' })

    const [isLoading, setIsLoading] = useState(false)
    const [isClientLoading, setIsClientLoading] = useState(false)
    // const navigate = useNavigate()
    const randomDeviceId = uuidv4();
    const [searchParams] = useSearchParams();
    const tokken = getCookies("Sadguru_HRMS_token")
    console.log("tokken", tokken)

    const onSubmit = async (data: ILoginData) => {
        setIsLoading(true);
        try {

            let deviceId = ""
            if (localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId')) {
                deviceId = localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId') ?? '';
            } else {
                deviceId = '2025_' + randomDeviceId;
            }
            localStorage.setItem(import.meta.env.VITE_BUSINESS + 'deviceId', deviceId)

            const payload: ILoginData = {
                email: data.email,
                password: data.password,
                deviceId: deviceId,
            }

            if (searchParams.get('client_id')) {
                payload.client_id = searchParams.get('client_id') ?? undefined;
                payload.redirect_uri = searchParams.get('redirect_uri') ?? undefined;
                payload.scope = searchParams.get('scope') ?? undefined;
                payload.state = searchParams.get('state') ?? undefined;
                payload.code_challenge = searchParams.get('code_challenge') ?? undefined;
                payload.code_challenge_method = searchParams.get('code_challenge_method') ?? undefined;
                payload.response_type = searchParams.get('response_type') ?? undefined;
            }

            const response = await AuthService?.login(payload)

            if (response?.status) {
                if (response?.data?.isNavigate) {
                    window.location.href = response?.data?.redirectUrl as string
                    return
                }

                showToastNotification("success", response?.message)
                setCookies(
                    import.meta.env.VITE_BUSINESS + 'SSO_token',
                    response?.data?.token?.accessToken as string,
                );
                setCookies(
                    import.meta.env.VITE_BUSINESS + 'SSO_refreshToken',
                    response?.data?.token?.refreshToken as string,
                );
                localStorage.setItem(
                    import.meta.env.VITE_BUSINESS + 'SSO_token',
                    response?.data?.token?.accessToken as string,
                );
            }
            else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsLoading(false);
        }

    }

    const getClientDetails = async () => {
        setIsClientLoading(true);
        try {
            const params: Record<string, string> = {};
            searchParams.forEach((value, key) => {
                params[key] = value;
            });
            console.log("Extracted SSO Params:", params);

            const queryString = new URLSearchParams(params).toString();
            const url = `${API_URL}${API_ENDPOINT.oauth}/authorize${queryString ? `?${queryString}` : ''}`;

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${tokken}`,
                    "Content-Type": "application/json",
                    // "ngrok-skip-browser-warning": "true",
                },
            });
            console.log("response", response)


            if (response?.data?.data?.isNavigate) {
                window.location.href = response?.data?.data?.redirectUrl as string
            } else {
                showToastNotification("danger", response?.data?.data?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsClientLoading(false);
        }
    }
    useEffect(() => {
        if (tokken) {
            getClientDetails()
        }

    }, [searchParams])

    return (
        <div className="min-h-screen w-full flex bg-slate-50 relative overflow-hidden">
            {isClientLoading && (
                <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white/30 backdrop-blur-md">
                    <div className="p-8 rounded-3xl bg-white/80 shadow-2xl flex flex-col items-center gap-4 border border-white">
                        <Spinner className="size-12 text-primary" />
                        <p className="text-slate-600 font-semibold animate-pulse">Processing your request...</p>
                    </div>
                </div>
            )}

            {/* Right side: Login Form */}
            <div className="w-full flex items-center justify-center p-6 relative">
                <div className="w-full max-w-md">
                    <div className="bg-white/80 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_8px_40px_-12px_rgba(0,0,0,0.1)] border border-white p-8 sm:p-10 transition-all duration-500 hover:shadow-[0_16px_60px_-15px_rgba(0,0,0,0.15)] relative overflow-hidden group">
                        {/* Decorative Orbs */}
                        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 rounded-full bg-linear-to-br from-indigo-500/20 to-primary/20 blur-2xl transition-transform duration-700 group-hover:scale-150"></div>
                        <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-32 h-32 rounded-full bg-linear-to-tr from-blue-500/20 to-primary/20 blur-2xl transition-transform duration-700 group-hover:scale-150"></div>

                        <div className="relative z-10 mb-8 flex flex-col items-center text-center">
                            <div className="w-14 h-14 bg-linear-to-tr from-indigo-500 to-primary text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/30 transform transition-transform duration-500 hover:rotate-12">
                                <Fingerprint size={28} strokeWidth={1.5} />
                            </div>
                            <h2 className="text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
                                Single Sign-On
                            </h2>
                            <p className="text-slate-500 font-medium max-w-sm">
                                Enter your organization's email or ID to access your workspace via SSO
                            </p>
                        </div>

                        <form className="relative z-10 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                            <div className="space-y-5">
                                <FormField
                                    label="Organization Email or ID"
                                    placeholder="e.g. name@company.com or org-id"
                                    type="text"
                                    {...register('email', {
                                        ...errorTypes.required('Organization ID or Email'),
                                    })}
                                    error={errors.email?.message}
                                />
                                <FormField
                                    type="password"
                                    label="Password"
                                    placeholder="••••••••"
                                    {...register('password', {
                                        ...errorTypes.required('Password'),
                                        ...errorTypes.password
                                    })}
                                    error={errors.password?.message}
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full "
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Redirecting...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2">
                                        Continue with SSO
                                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
                                    </div>
                                )}
                            </Button>
                        </form>

                        <div className="relative z-10 my-8 flex items-center gap-4">
                            <div className="flex-1 h-px bg-linear-to-r from-transparent via-slate-200 to-transparent"></div>
                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest bg-white/80 px-2 rounded-full">Or</div>
                            <div className="flex-1 h-px bg-linear-to-r from-transparent via-slate-200 to-transparent"></div>
                        </div>

                        <div className="relative z-10 text-center">
                            <Link
                                to={RoutesLink.authRoutes.login}
                                className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-600 hover:text-primary transition-colors px-4 py-2 rounded-lg hover:bg-primary/5"
                            >
                                <ArrowRight className="h-4 w-4 rotate-180" />
                                Back to regular Sign In
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SsoLogin